
/**
 * Dashboard Page - Production Grade
 * "Industrial Precision" Aesthetic
 */

import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Shield,
  Plus,
  Server,
  RefreshCw,
  Zap,
  FileText
} from 'lucide-react'
import { useDashboardStats } from './hooks/useDashboard'
import { useUIStore } from '@/stores/ui-store'
import { DashboardMetric } from './components/DashboardMetric'
import { DashboardActivityLog } from './components/DashboardActivityLog'
import { DashboardTopTenants } from './components/DashboardTopTenants'

// ============================================================================
// MAIN PAGE
// ============================================================================
export function DashboardPage() {
  const { data: stats, isLoading, refetch } = useDashboardStats()
  const { openModal } = useUIStore()

  // Safely access data or default to empty
  const overview = stats?.overview || {
    totalLicenses: 0,
    activeLicenses: 0,
    activeTenants: 0,
    totalTenants: 0,
    monthlyRevenue: 0,
    revenueGrowth: 0
  }

  const activity = stats?.activity || []
  const topTenants = stats?.topTenants || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">System Status</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className="flex h-2 w-2 rounded-full bg-emerald-500" />
            <p className="text-sm text-muted-foreground font-mono">
              OPERATIONAL
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2 h-9">
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button size="sm" onClick={() => openModal('create-license')} className="gap-2 h-9 bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
            <Plus className="h-3.5 w-3.5" />
            New License
          </Button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid gap-px bg-border border rounded-lg overflow-hidden md:grid-cols-4 shadow-sm">
        <DashboardMetric 
          label="Active Licenses" 
          value={overview.activeLicenses} 
          subValue={`/ ${overview.totalLicenses} Total`}
          loading={isLoading}
        />
        <DashboardMetric 
          label="Connected Tenants" 
          value={overview.activeTenants} 
          subValue={`${overview.totalTenants} Registered`}
          loading={isLoading}
        />
        <DashboardMetric 
          label="MRR (Est.)" 
          value={overview.monthlyRevenue.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 })} 
          subValue="vs last month"
          trend={overview.revenueGrowth}
          loading={isLoading}
        />
        <div className="flex flex-col p-5 bg-card hover:bg-muted/5 transition-colors group">
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">Quick Actions</p>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-auto">
             <Button variant="outline" size="sm" className="w-full justify-start h-10 px-3 overflow-hidden" asChild>
               <Link to="/dashboard/licenses" className="flex items-center min-w-0">
                 <Shield className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
                 <div className="flex flex-col items-start min-w-0">
                   <span className="font-medium text-xs truncate w-full">Licenses</span>
                 </div>
               </Link>
             </Button>
             <Button variant="outline" size="sm" className="w-full justify-start h-10 px-3 overflow-hidden" asChild>
               <Link to="/dashboard/tenants" className="flex items-center min-w-0">
                 <Server className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
                 <div className="flex flex-col items-start min-w-0">
                   <span className="font-medium text-xs truncate w-full">Tenants</span>
                 </div>
               </Link>
             </Button>
             <Button variant="outline" size="sm" className="w-full justify-start h-10 px-3 overflow-hidden" asChild>
               <Link to="/dashboard/billing" className="flex items-center min-w-0">
                 <FileText className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
                 <div className="flex flex-col items-start min-w-0">
                   <span className="font-medium text-xs truncate w-full">Invoices</span>
                 </div>
               </Link>
             </Button>
             <Button variant="outline" size="sm" className="w-full justify-start h-10 px-3 overflow-hidden" asChild>
               <Link to="/dashboard/extensions" className="flex items-center min-w-0">
                 <Zap className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
                 <div className="flex flex-col items-start min-w-0">
                   <span className="font-medium text-xs truncate w-full">Exts</span>
                 </div>
               </Link>
             </Button>
          </div>
        </div>
      </div>

      {/* Content Columns */}
      <div className="grid gap-6 lg:grid-cols-3 h-[400px]">
        <div className="lg:col-span-2 h-full">
           <DashboardActivityLog activities={activity} loading={isLoading} />
        </div>
        <div className="h-full">
           <DashboardTopTenants tenants={topTenants} loading={isLoading} />
        </div>
      </div>
    </div>
  )
}
